import axios from 'axios';

/**
 * Resolve the backend base URL with defense-in-depth.
 *
 * Behaviour:
 *  - If REACT_APP_BACKEND_URL is set AND its host matches the page host, use it.
 *  - In local dev (localhost / 127.0.0.1), always trust the env var as-is.
 *  - Otherwise (deployed env where the env var was misconfigured / points to a
 *    non-resolving host), fall back to the page's own origin. Emergent's
 *    ingress routes `/api/*` to the backend on the same host that serves the
 *    frontend, so `window.location.origin` is always a safe target.
 */
export const resolveBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (typeof window === 'undefined') return envUrl;

  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1';
  if (isLocalhost) return envUrl;

  if (envUrl) {
    try {
      const envHost = new URL(envUrl).host;
      if (envHost === window.location.host) return envUrl;
    } catch (_) {
      // ignore malformed env URL, fall through to origin
    }
  }
  return window.location.origin;
};

export const BACKEND_URL = resolveBackendUrl();
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const actAs = sessionStorage.getItem('agency_act_as');
    if (actAs && (config.url || '').includes('/agency/')) {
      config.headers['X-Agency-Id'] = actAs;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login') || 
                             error.config?.url?.includes('/auth/candidate/register');
      const isOnLoginPage = window.location.pathname === '/login' || 
                            window.location.pathname === '/verify';
      
      if (!isLoginRequest && !isOnLoginPage) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
//
// Login retry-with-backoff:
//   Production occasionally serves a Cloudflare 520 "could not parse" on the
//   very first login of a cold backend pod (most often after a deploy or an
//   autoscaler scale-up). The backend is now hardened to always return clean
//   JSON, but for any historic build still in cache OR a genuine transient
//   network blip we silently retry up to twice with exponential backoff
//   before surfacing the error to the user. Each attempt is ~150ms +
//   backoff, so the worst case is sub-2s.
const _shouldRetryLogin = (err) => {
  if (!err) return true;
  // No response at all → likely a transient network failure
  if (!err.response) return true;
  const s = err.response.status;
  // 5xx + 408 (timeout) + 429 (rate-limit, after backoff) are retryable
  return s >= 500 || s === 408 || s === 429;
};

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const authAPI = {
  login: async (credentials) => {
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        return await api.post('/auth/login', credentials);
      } catch (err) {
        lastErr = err;
        if (!_shouldRetryLogin(err) || attempt === 2) throw err;
        // 400ms, 1200ms — keeps total worst-case under ~2s for a 3-attempt cycle.
        await _sleep(400 + 800 * attempt);
      }
    }
    throw lastErr;
  },
  registerCandidate: (data) => api.post('/auth/candidate/register', data),
  getPortalInfo: (token) => api.get(`/candidate/portal-info/${token}`),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Candidate Wizard APIs (Sprint 1 + 2: full 6-step onboarding)
export const candidateWizardAPI = {
  getState: () => api.get('/candidate/wizard'),
  saveDocVault: (data) => api.post('/candidate/wizard/doc-vault', data, { timeout: 180000 }),
  skipDocVault: () => api.post('/candidate/wizard/doc-vault/skip'),
  idContinue: () => api.post('/candidate/wizard/id/continue'),
  saveBasicDetails: (data) => api.post('/candidate/wizard/basic-details', data),
  saveAadhaar: (data) => api.post('/candidate/wizard/id/aadhaar', data),
  savePan: (data) => api.post('/candidate/wizard/id/pan', data),
  saveEducationDocs: (data) => api.post('/candidate/wizard/education-docs', data),
  saveEmploymentDocs: (data) => api.post('/candidate/wizard/employment-docs', data),
  saveSelfie: (data) => api.post('/candidate/wizard/selfie', data),
  finalSubmit: (data) => api.post('/candidate/wizard/final-submit', data),
};

// HRMS Connector admin APIs
export const connectorAdminAPI = {
  listKeys: () => api.get('/admin/connector/keys'),
  createKey: (data) => api.post('/admin/connector/keys', data),
  updateKey: (id, data) => api.patch(`/admin/connector/keys/${id}`, data),
  deleteKey: (id) => api.delete(`/admin/connector/keys/${id}`),
  activity: (keyId) => api.get('/admin/connector/activity', { params: keyId ? { key_id: keyId } : {} }),
};

// Admin APIs
export const adminAPI = {
  setupAdmin: () => api.post('/admin/setup'),
  getCompanies: () => api.get('/admin/companies'),
  createCompany: (data) => api.post('/admin/companies', data),
  updateCompanyBranding: (id, data) => api.patch(`/admin/companies/${id}/branding`, data),
  updateCompanyKycProviders: (id, config) => api.patch(`/admin/companies/${id}/kyc-providers`, { kyc_provider_config: config }),
  updateAgencySettings: (id, payload) => api.patch(`/admin/companies/${id}/agency-settings`, payload),
  getAllBGVRequests: (params = {}) => api.get('/admin/bgv-requests', { params }),
  quickSearchCases: (params = {}) => api.get('/admin/analytics/quick-search', { params }),
  listTags: () => api.get('/admin/tags'),
  updateCaseTags: (caseRef, tags) => api.patch(`/admin/cases/${encodeURIComponent(caseRef)}/tags`, { tags }),
  // Business Rules Engine
  rulesSchema: () => api.get('/admin/rules/schema'),
  listRules: () => api.get('/admin/rules'),
  getRule: (id) => api.get(`/admin/rules/${id}`),
  createRule: (payload) => api.post('/admin/rules', payload),
  updateRule: (id, payload) => api.put(`/admin/rules/${id}`, payload),
  toggleRule: (id) => api.post(`/admin/rules/${id}/toggle`),
  deleteRule: (id) => api.delete(`/admin/rules/${id}`),
  testRule: (id, ctx) => api.post(`/admin/rules/${id}/test`, ctx),
  ruleExecutions: (params = {}) => api.get('/admin/rules/executions', { params }),
  // OOB HRMS Connectors
  hrmsProviders: () => api.get('/admin/hrms-connectors/providers'),
  listHrmsConnectors: () => api.get('/admin/hrms-connectors'),
  createHrmsConnector: (payload) => api.post('/admin/hrms-connectors', payload),
  updateHrmsConnector: (id, payload) => api.put(`/admin/hrms-connectors/${id}`, payload),
  deleteHrmsConnector: (id) => api.delete(`/admin/hrms-connectors/${id}`),
  testHrmsConnector: (id) => api.post(`/admin/hrms-connectors/${id}/test`),
  testHrmsConnectorAdhoc: (payload) => api.post('/admin/hrms-connectors/test-adhoc', payload),
  syncHrmsConnector: (id) => api.post(`/admin/hrms-connectors/${id}/sync`),
  seedDemo: () => api.post('/admin/seed-demo'),
  cxoOverview: () => api.get('/admin/cxo/overview'),
  cxoGeo: () => api.get('/admin/cxo/geo'),
  ensureDemo: () => api.post('/admin/ensure-demo'),
  previewHrmsCandidates: (id, limit = 50) => api.get(`/admin/hrms-connectors/${id}/preview`, { params: { limit } }),
  importHrmsCandidates: (id, payload) => api.post(`/admin/hrms-connectors/${id}/import`, payload),
  exportBGVRequestsCSV: (params = {}) => api.get('/admin/bgv-requests/export.csv', { params, responseType: 'blob' }),
  // Case Management
  getCaseDetail: (caseRef) => api.get(`/admin/cases/${encodeURIComponent(caseRef)}`),
  getProofMatrix: (caseRef) => api.get(`/admin/cases/${encodeURIComponent(caseRef)}/proof-matrix`),
  getCaseDiff: (caseRef) => api.get(`/admin/cases/${encodeURIComponent(caseRef)}/diff`),
  getCaseWorkflow: (caseRef) => api.get(`/admin/cases/${encodeURIComponent(caseRef)}/workflow`),
  addCaseNote: (caseRef, text) => api.post(`/admin/cases/${encodeURIComponent(caseRef)}/notes`, { text }),
  updateCaseStatus: (caseRef, status, reason) => api.patch(`/admin/cases/${encodeURIComponent(caseRef)}/status`, { status, reason }),
  autoCloseCases: () => api.post('/admin/cases/auto-close-sweep'),
  resendCandidateLink: (bgvId) => api.post(`/admin/bgv-requests/${bgvId}/resend-link`),
  // Bulk actions (max 100 case_ids per call)
  bulkAssignAgent: (data) => api.post('/admin/bgv-requests/bulk-assign-agent', data),
  listFieldAgents: () => api.get('/admin/field-agents'),
  bulkChangeStatus: (data) => api.post('/admin/bgv-requests/bulk-status', data),
  bulkRemind: (data) => api.post('/admin/bgv-requests/bulk-remind', data),
  bulkExportCsv: (data) => api.post('/admin/bgv-requests/bulk-export-csv', data, { responseType: 'blob' }),
  // Saved Views (Tier 1 ITSM)
  listSavedViews: () => api.get('/admin/saved-views'),
  createSavedView: (data) => api.post('/admin/saved-views', data),
  updateSavedView: (id, data) => api.patch(`/admin/saved-views/${id}`, data),
  deleteSavedView: (id) => api.delete(`/admin/saved-views/${id}`),
  // Case Amendments
  getCaseAmendments: (caseRef) => api.get(`/admin/cases/${encodeURIComponent(caseRef)}/amendments`),
  addCaseAmendment: (caseRef, data) => api.post(`/admin/cases/${encodeURIComponent(caseRef)}/amendments`, data),
  agentVerifyCase: (caseRef, verified, notes) => api.post(`/admin/cases/${encodeURIComponent(caseRef)}/agent-verify`, { verified, notes }),
  agentVerifyDoc: (caseRef, section, key, verified, notes, label) =>
    api.post(`/admin/cases/${encodeURIComponent(caseRef)}/agent-verify-doc`,
      { section, key, verified, notes: notes || '', label: label || '' }),
  // Trigger VerifyPro-AI auto-review for an existing case (fire-and-forget on backend).
  runVerifyProAI: (caseRef) => api.post(`/admin/cases/${encodeURIComponent(caseRef)}/run-verifypro-ai`),  // Document fetch: returns axios blob promise so caller can open as ObjectURL
  fetchCaseDocument: (caseRef, section, key) => api.get(
    `/admin/cases/${encodeURIComponent(caseRef)}/document/${encodeURIComponent(section)}/${encodeURIComponent(key)}`,
    { responseType: 'blob' }
  ),
  meonDigilockerLink: (caseRef, payload) => api.post(`/admin/cases/${encodeURIComponent(caseRef)}/meon/digilocker/link`, payload || {}),
  meonDigilockerFetch: (caseRef) => api.post(`/admin/cases/${encodeURIComponent(caseRef)}/meon/digilocker/fetch`, {}),
  meonDigilockerStatus: (caseRef) => api.get(`/admin/cases/${encodeURIComponent(caseRef)}/meon/digilocker`),
  deleteBGVRequest: (id, payload) => api.delete(`/admin/bgv-requests/${id}`, { data: payload }),
  bulkUploadBGVs: (companyId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('company_id', companyId);
    return api.post('/company/bgv-requests/bulk-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  bulkTemplateUrl: (fmt = 'csv') => `${API_BASE}/company/bgv-requests/bulk-template?fmt=${fmt}`,

  // Company Users (HR)
  createCompanyUser: (companyId, data) => api.post(`/admin/companies/${companyId}/users`, data),
  listCompanyUsers: (companyId) => api.get(`/admin/companies/${companyId}/users`),
  // M:N linked users (includes both new-style links + legacy company_id users)
  listCompanyLinkedUsers: (companyId) => api.get(`/admin/companies/${companyId}/linked-users`),
  linkExistingUserToCompany: (companyId, data) =>
    api.post(`/admin/companies/${companyId}/linked-users`, data),
  
  // Field Agent Management
  createFieldAgent: (data) => api.post('/admin/field-agents', data),
  getFieldAgents: () => api.get('/admin/field-agents'),
  purgeSoftDeletedAgents: () => api.post('/admin/field-agents/purge-soft-deleted'),
  deleteFieldAgent: (id, opts = {}) => api.delete(`/admin/field-agents/${id}${opts.force ? '?force=true' : ''}`),
  createAssignment: (data) => api.post('/admin/field-assignments', data),
  getAllAssignments: () => api.get('/admin/field-assignments'),

  // SLA + Analytics
  getSlaBreaches: () => api.get('/admin/sla/breaches'),
  bulkDeleteBGV: (payload) => api.post('/admin/bgv-requests/bulk-delete', payload),
  triggerSlaCheck: () => api.post('/admin/sla/check-breaches'),
  getAnalytics: (params = {}) => api.get('/admin/analytics/overview', { params }),
  getAnalyticsTimeseries: (params = {}) => {
    // Backward-compatible: accept plain `days` number or a params dict.
    const cfg = typeof params === 'number' ? { days: params } : params;
    return api.get('/admin/analytics/timeseries', { params: cfg });
  },

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),

  // Integrations plugin system
  listIntegrationTemplates: () => api.get('/admin/integrations/templates'),
  listIntegrations: () => api.get('/admin/integrations'),
  getIntegration: (id) => api.get(`/admin/integrations/${id}`),
  createIntegration: (data) => api.post('/admin/integrations', data),
  updateIntegration: (id, data) => api.put(`/admin/integrations/${id}`, data),
  deleteIntegration: (id) => api.delete(`/admin/integrations/${id}`),
  testIntegration: (id) => api.post(`/admin/integrations/${id}/test`),

  // DPDP / Compliance
  getAuditLogs: (params = {}) => api.get('/admin/audit-logs', { params }),
  getDeletionRequests: () => api.get('/admin/deletion-requests'),
  resolveDeletionRequest: (id, action, notes) =>
    api.post(`/admin/deletion-requests/${id}/resolve`, { action, notes }),
  runRetention: () => api.post('/admin/retention/run'),
};

// Public APIs (no auth)
export const publicAPI = {
  getDpdpInfo: () => api.get('/public/dpdp-info'),
};

// Candidate (extra)
export const candidateExtraAPI = {
  getMyData: () => api.get('/candidate/my-data'),
  requestDeletion: (reason) => api.post('/candidate/delete-request', { reason }),
};

// Company APIs
export const companyAPI = {
  createBGVRequest: (data) => api.post('/company/bgv-requests', data),
  bulkCreateBGVRequests: (data) => api.post('/company/bgv-requests/bulk', data),
  parseEmailIntake: (data) => api.post('/company/email-intake/parse', data),
  getBGVRequests: () => api.get('/company/bgv-requests'),
  getProfile: () => api.get('/company/profile'),
  getAnalytics: () => api.get('/company/dashboard/analytics'),
  getStory: () => api.get('/company/dashboard/story'),
  getConstellation: () => api.get('/company/dashboard/constellation'),
  getGeo: () => api.get('/company/dashboard/geo'),
  searchCases: (params = {}) => api.get('/company/cases/search', { params }),
  getCaseDetail: (ref) => api.get(`/company/cases/${encodeURIComponent(ref)}`),
};

// Corporate Sector APIs (company_category === 'corporate')
export const corporateAPI = {
  dashboard: () => api.get('/corporate/dashboard'),
  cases: (params = {}) => api.get('/corporate/cases', { params }),
};

// KYB (Know Your Business) APIs — agency tenants
export const kybAPI = {
  run: (payload) => api.post('/agency/kyb/run', payload),
  cases: (params = {}) => api.get('/agency/kyb/cases', { params }),
  caseDetail: (id) => api.get(`/agency/kyb/cases/${id}`),
};

// Candidate APIs
export const candidateAPI = {
  getBGVRequest: () => api.get('/candidate/bgv-request'),
  getMySubmissions: () => api.get('/candidate/my-submissions'),
  downloadReportUrl: (reportId) => `${API_BASE}/reports/download/${reportId}`,
};

// Surepass Self-Verification APIs
export const surepassAPI = {
  getConfig: () => api.get('/surepass/config'),
  getStatus: () => api.get('/candidate/self-verify/status'),
  selfVerify: (checkType, payload) => api.post(`/candidate/self-verify/${checkType}`, payload),
  // Admin
  adminGetAttempts: (bgvId) => api.get(`/admin/bgv-requests/${bgvId}/surepass`),
  adminReset: (bgvId, checkType) => api.post(`/admin/bgv-requests/${bgvId}/surepass/reset/${checkType}`),
};

// DigiLocker (Surepass DigiBoost) APIs
export const digilockerAPI = {
  initialize: () => api.post('/candidate/wizard/digilocker/initialize'),
  fetch: (clientId) => api.post('/candidate/wizard/digilocker/fetch', { client_id: clientId }),
  // Meon DigiLocker (consent-link flow) — candidate's own case.
  meonLink: () => api.post('/candidate/wizard/digilocker/meon/link'),
  meonFetch: (state) => api.post('/candidate/wizard/digilocker/meon/fetch', state ? { state } : {}),
};

// Field Agent APIs
export const agentAPI = {
  getAssignments: () => api.get('/agent/assignments'),
  // Tri-panel Zoho-style detail: what was requested, what candidate submitted,
  // what has already been verified (evidence + cross-verify).
  getCaseDetail: (caseRef) => api.get(`/agent/cases/${caseRef}`),
  getProofMatrix: (caseRef) => api.get(`/agent/cases/${caseRef}/proof-matrix`),
  submitEvidence: (data) => api.post('/agent/submit-evidence', data),
  postPing: (data) => api.post('/agent/pings', data),
  getPendingNudges: () => api.get('/agent/nudges/pending'),
  ackNudges: () => api.post('/agent/nudges/ack'),
};

// Workflow Packages
export const workflowPackagesAPI = {
  list: (activeOnly = true) => api.get('/workflow-packages', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/admin/workflow-packages', data),
  update: (id, data) => api.patch(`/admin/workflow-packages/${id}`, data),
  remove: (id) => api.delete(`/admin/workflow-packages/${id}`),
};

// User Management (Admin-only)
export const usersAPI = {
  list: (params) => api.get('/admin/users', { params }),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.patch(`/admin/users/${id}`, data),
  resetPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  deactivate: (id) => api.delete(`/admin/users/${id}`),
  // Hard delete — physically removes the user doc so the same email can be
  // reused. QA-only. Backend also purges related auth artefacts (reset tokens).
  hardDelete: (id) => api.delete(`/admin/users/${id}`, { params: { force: true } }),
  assignRole: (id, data) => api.patch(`/admin/users/${id}/role`, data),
  toggleSentinel: (id, enabled) => api.patch(`/admin/users/${id}/sentinel-access`, { enabled }),
  toggleArgus: (id, enabled) => api.patch(`/admin/users/${id}/argus-access`, { enabled }),
  // Bidirectional M:N user↔company mapping
  listCompanies: (userId) => api.get(`/admin/users/${userId}/companies`),
  linkCompany: (userId, data) => api.post(`/admin/users/${userId}/companies`, data),
  updateCompanyLink: (userId, companyId, data) =>
    api.patch(`/admin/users/${userId}/companies/${companyId}`, data),
  unlinkCompany: (userId, companyId) => api.delete(`/admin/users/${userId}/companies/${companyId}`),
  // From the company side
  listLinkedUsers: (companyId) => api.get(`/admin/companies/${companyId}/linked-users`),
  linkExistingUser: (companyId, data) =>
    api.post(`/admin/companies/${companyId}/linked-users`, data),
};

// RBAC — roles & permissions (Admin only)
export const rolesAPI = {
  permissionsCatalog: () => api.get('/admin/permissions-catalog'),
  list: (params) => api.get('/admin/roles', { params }),
  create: (data) => api.post('/admin/roles', data),
  update: (id, data) => api.patch(`/admin/roles/${id}`, data),
  remove: (id) => api.delete(`/admin/roles/${id}`),
  mePermissions: () => api.get('/me/permissions'),
};

// VerifyPro Sentinel — ambient AI avatar (voice + gesture)
export const sentinelAPI = {
  health: () => api.get('/sentinel/health'),
  command: (payload) => api.post('/sentinel/command', payload),
};

// Field Agent Trust Profile
export const agentTrustAPI = {
  // Admin — full profile with PII + recent ratings
  adminProfile: (agentId) => api.get(`/admin/field-agents/${agentId}/trust-profile`),
  // HR-safe subset (no PII) — used by company case view + maps popover for HR
  companyCard:  (agentId) => api.get(`/company/agents/${agentId}/trust-card`),
  // Agent viewing their own profile
  mine:         () => api.get('/agent/me/trust-profile'),
  // Admin gives a 1-5 star rating for a completed case
  rate:         (caseRef, data) => api.post(`/admin/cases/${caseRef}/rate-agent`, data),
};

// Company hard-delete (Admin-only)
export const companiesAPI = {
  hardDelete: (id, { cascade = false } = {}) =>
    api.delete(`/admin/companies/${id}`, { params: { force: true, cascade } }),
  softDelete: (id) => api.delete(`/admin/companies/${id}`),
};

// Web Push notifications (Field Agent + future roles)
export const notificationsAPI = {
  vapidKey: () => api.get('/notifications/vapid-public-key'),
  status: () => api.get('/notifications/status'),
  subscribe: (subscription) => api.post('/notifications/subscribe', { subscription }),
  unsubscribe: (subscription) => api.post('/notifications/unsubscribe', { subscription }),
  test: () => api.post('/notifications/test'),
};

// PDF Signature Check (Gemini Vision)
export const signatureCheckAPI = {
  run: (caseRef, section, key) => api.post(`/admin/cases/${encodeURIComponent(caseRef)}/signature-check/${section}/${key}`),
};

// Maps APIs (OpenStreetMap / Nominatim / OSRM)
export const mapsAPI = {
  agentLocations: (params = {}) => api.get('/admin/field-agents/locations', { params }),
  checkAgentStatus: (agentUserId) => api.post(`/admin/field-agents/${agentUserId}/check-status`),
  reverseGeocode: (lat, lng) => api.get('/admin/reverse-geocode', { params: { lat, lng } }),
  route: (fromLat, fromLng, toLat, toLng) =>
    api.get('/admin/route', { params: { from_lat: fromLat, from_lng: fromLng, to_lat: toLat, to_lng: toLng } }),
};

// Document APIs
export const documentAPI = {
  upload: (data) => api.post('/documents/upload', data),
  getByBGV: (bgvId) => api.get(`/documents/bgv/${bgvId}`),
};

// Report APIs
export const reportAPI = {
  generate: (bgvId) => api.post(`/reports/generate/${bgvId}`),
  download: (reportId) => `${API_BASE}/reports/download/${reportId}`,
  getInfo: (bgvId) => api.get(`/reports/bgv/${bgvId}`),
};

// ── AI-Ops + Dashboard Studio ────────────────────────────────────────
export const aiOpsAPI = {
  companyBrief: () => api.post('/company/ai-ops/brief'),
  adminBrief: () => api.post('/admin/ai-ops/brief'),
  escalateCase: (caseId, data) => api.post(`/company/cases/${caseId}/escalate`, data),
  prioritizeCase: (caseId, data) => api.post(`/company/cases/${caseId}/prioritize`, data),
  nudgeCandidate: (caseId, data) => api.post(`/company/cases/${caseId}/nudge`, data),
  analyzeCase: (caseId) => api.post(`/company/cases/${caseId}/analyze`),
  dispatchAgent: (caseId, data) => api.post(`/company/cases/${caseId}/dispatch-agent`, data),
};

export const dashboardsAPI = {
  list: () => api.get('/dashboards'),
  get: (id) => api.get(`/dashboards/${id}`),
  create: (data) => api.post('/dashboards', data),
  update: (id, data) => api.patch(`/dashboards/${id}`, data),
  clone: (id, data) => api.post(`/dashboards/${id}/clone`, data),
  delete: (id) => api.delete(`/dashboards/${id}`),
  data: (id) => api.get(`/dashboards/${id}/data`),
  widgetCatalog: () => api.get('/dashboards/widget-catalog'),
  pdfUrl: (id) => `${API_BASE}/dashboards/${id}/export.pdf`,
};

export default api;
